import { useEffect, useMemo, useRef, useState } from "react";
import { Modal, Button } from "@common";
import { formatMoney } from "@utils";
import CurrencyBadge from "../CurrencyBadge";
import {
  COUNT_LABELS,
  PAYMENT_PLANS,
  addDays,
  formValuesToPlan,
  nonBankingReason,
  parseISO,
  planNonBankingDays,
  planToFormValues,
  planTotal,
  planWindowIssue,
  prevBankingDay,
  resolvePlan,
  toISO,
  toSplitsPayload,
} from "./statementPlan";

// ─── field primitives (mirroring SchedulePaymentModal) ───────────────────────

const FIELD_CLS =
  "w-full rounded-xl border border-palette-gray-divider bg-palette-gray-subtle px-3.5 py-2.5 text-sm text-palette-gray-primary placeholder:text-palette-gray-placeholder focus:border-palette-indigo-ring focus:outline-none focus:ring-2 focus:ring-palette-indigo-border transition-colors";

function Label({ children }) {
  return (
    <label className="mb-1.5 block text-sm font-medium text-palette-gray-heading">{children}</label>
  );
}

function SelectField({ label, value, onChange, options, placeholder }) {
  return (
    <div>
      {label && <Label>{label}</Label>}
      <div className="relative">
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={`${FIELD_CLS} appearance-none pr-9`}
        >
          {placeholder != null && <option value="">{placeholder}</option>}
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <svg
          className="pointer-events-none absolute right-3.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-palette-gray-secondary"
          viewBox="0 0 10 6"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="M1 1l4 4 4-4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
    </div>
  );
}

function DateField({ label, value, onChange, min, max }) {
  return (
    <div>
      {label && <Label>{label}</Label>}
      <input
        type="date"
        value={value ?? ""}
        min={min || undefined}
        max={max || undefined}
        onChange={(e) => onChange(e.target.value)}
        className={FIELD_CLS}
      />
    </div>
  );
}

function MoneyField({ label, value, onChange }) {
  return (
    <div>
      {label && <Label>{label}</Label>}
      <input
        type="number"
        inputMode="decimal"
        min="0"
        step="0.01"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="0.00"
        className={FIELD_CLS}
      />
    </div>
  );
}

const EMPTY_FORM = {
  from_bank_account: "",
  to_card_account: "",
  statement_date: "",
  due_date: "",
  balance: "",
  payment_plan: "full_on_due",
  plan_count: "",
  single_date: "",
  custom: [{ date: "", amount: "" }],
  notes: "",
};

function fmtPreviewDate(d) {
  return d.toLocaleDateString("en-CA", { month: "short", day: "numeric", year: "numeric" });
}

/**
 * "Add statement" modal: a dated card statement plus the plan that pays it.
 *
 * The resulting-payments panel is the point of the whole form — every field
 * feeds it, and it recomputes on each keystroke so the split is visible before
 * saving rather than discovered afterwards. Saving is blocked until the plan
 * accounts for the full balance, because a plan whose parts do not sum to the
 * whole would under- or over-state the outflow with nothing to flag it, and until
 * every payment falls inside the statement → due window.
 */
export default function StatementPaymentModal({
  isOpen,
  onClose,
  onSave,
  bankAccounts = [],
  cardAccounts = [],
  initialValues = null,
  saving = false,
  isEdit = false,
  // Saved plans (StatementPlan) for the cards in scope, at most one per card.
  // Selecting a card that has one fills this whole form in, leaving the amount.
  plans = [],
  // The month the derived statement/due dates belong to, "2026-09". The list
  // page's own month filter, so "add a statement" means "for the month I am
  // looking at".
  month = null,
  // Saves the statement and stores its shape as the card's plan, in one click.
  onSaveWithPlan = null,
  savingPlan = false,
}) {
  const [form, setForm] = useState(EMPTY_FORM);

  // The splits AS STORED, captured when the modal opens.
  //
  // An existing statement's payments can have been moved on the cash-flow grid,
  // and the plan rule cannot reproduce that: re-deriving "3 business days from
  // Aug 27" always yields Aug 27/28/31, even after one was moved to Aug 30. So
  // the stored list wins until a field that actually defines the plan is
  // changed. Without this the modal showed the pre-move dates AND silently
  // reverted the move on save, since the payload is built from this preview.
  const [storedSplits, setStoredSplits] = useState([]);
  const [planDirty, setPlanDirty] = useState(false);

  // The saved plan currently filling this form, if any — kept so the notice can
  // say where these values came from. A pre-filled form with no explanation reads
  // as stale data.
  const [appliedPlan, setAppliedPlan] = useState(null);
  // Which card the plan has been applied for. Without this, re-rendering while
  // the same card is selected would re-apply the plan and wipe the amount just
  // typed.
  const appliedForCard = useRef(null);

  const planByCard = useMemo(() => {
    const map = new Map();
    (plans ?? []).forEach((p) => map.set(String(p.to_card_account), p));
    return map;
  }, [plans]);

  useEffect(() => {
    if (!isOpen) {
      // Reset on CLOSE, not just on open. Leaving the last card in `form` made the
      // plan-apply effect below re-apply the PREVIOUS card: on the commit where
      // the modal reopens, that effect still reads `form` from the render showing
      // the old values, so opening for card B applied card A's plan over it.
      setForm(EMPTY_FORM);
      setStoredSplits([]);
      setPlanDirty(false);
      setAppliedPlan(null);
      appliedForCard.current = null;
      return;
    }

    const init = { ...EMPTY_FORM, ...(initialValues ?? {}) };
    setForm(init);
    setStoredSplits((initialValues?.custom ?? []).filter((s) => s.date));
    setPlanDirty(false);

    // The card the modal OPENS on is already settled — an edit owns its stored
    // values, and a plan-seeded create was filled in by the caller. Recording it
    // here stops the effect below from treating it as a fresh selection and
    // re-deriving what is already there.
    const openCard = String(init.to_card_account ?? "");
    appliedForCard.current = openCard || null;
    // Seeded directly rather than left to that effect, so the "filled from the
    // saved plan" notice appears on the opening frame.
    setAppliedPlan(!isEdit && openCard ? (planByCard.get(openCard) ?? null) : null);
  }, [isOpen, initialValues, isEdit, planByCard]);

  const planForCard = planByCard.get(String(form.to_card_account || "")) ?? null;

  // Picking a card that has a saved plan fills the form from it. Create only —
  // an existing statement owns its own values, and a plan must never silently
  // rewrite what was already saved.
  useEffect(() => {
    if (!isOpen || isEdit) return;
    const cardKey = String(form.to_card_account || "");
    if (!cardKey || appliedForCard.current === cardKey) return;
    appliedForCard.current = cardKey;

    const plan = planByCard.get(cardKey);
    if (!plan) {
      setAppliedPlan(null);
      return;
    }
    setForm((f) => ({ ...f, ...planToFormValues(plan, month) }));
    // The rule owns the preview from here: the plan describes how to split, not
    // a stored list of payments.
    setPlanDirty(true);
    setAppliedPlan(plan);
  }, [isOpen, isEdit, form.to_card_account, planByCard, month]);

  const set = (key) => (val) => setForm((f) => ({ ...f, [key]: val }));
  // Fields that DEFINE the split. Touching one means the user is re-planning,
  // so the rule takes over from the stored dates.
  const setPlan = (key) => (val) => {
    setPlanDirty(true);
    set(key)(val);
  };

  const bankOptions = useMemo(
    () => bankAccounts.map((a) => ({ value: a.id, label: a.label })),
    [bankAccounts],
  );
  const cardOptions = useMemo(
    () => cardAccounts.map((a) => ({ value: a.id, label: a.label })),
    [cardAccounts],
  );

  const selectedCard = useMemo(
    () => cardAccounts.find((a) => String(a.id) === String(form.to_card_account)) ?? null,
    [cardAccounts, form.to_card_account],
  );

  // Due date must fall after the statement date. The picker enforces it with a
  // `min` rather than accepting an invalid pair and rejecting it on save.
  const minDueDate = useMemo(() => {
    const s = parseISO(form.statement_date);
    return s ? toISO(addDays(s, 1)) : "";
  }, [form.statement_date]);

  // Clear a due date that the statement date has overtaken, so the field cannot
  // keep showing a value the picker would no longer allow.
  useEffect(() => {
    if (form.statement_date && form.due_date && form.due_date <= form.statement_date) {
      setForm((f) => ({ ...f, due_date: "" }));
    }
  }, [form.statement_date, form.due_date]);

  // What the Payment date field shows until it is touched. Mirrors the fallback
  // `resolvePlan` uses for an untyped single payment, so the field and the preview
  // below it never disagree about the same date.
  const defaultSingleDate = useMemo(() => {
    const due = parseISO(form.due_date);
    return due ? toISO(prevBankingDay(due)) : "";
  }, [form.due_date]);

  const needsCount = COUNT_LABELS[form.payment_plan] != null;
  const isCustom = form.payment_plan === "custom_split";
  const isSingle = form.payment_plan === "single_payment";

  const derived = useMemo(
    () =>
      resolvePlan({
        plan: form.payment_plan,
        balance: form.balance,
        dueDate: form.due_date,
        count: form.plan_count,
        singleDate: form.single_date,
        customSplits: form.custom,
      }),
    [form],
  );

  // Stored dates until the plan is re-specified, then the rule.
  const payments = useMemo(() => {
    if (planDirty || storedSplits.length === 0) return derived;
    return storedSplits
      .map((s) => ({ date: parseISO(s.date), iso: s.date, amount: Number(s.amount) }))
      .filter((p) => p.date && Number.isFinite(p.amount))
      .sort((a, b) => (a.iso < b.iso ? -1 : 1));
  }, [planDirty, storedSplits, derived]);

  const total = planTotal(payments);
  const balanceNum = Number(form.balance) || 0;
  const totalMatches = payments.length > 0 && Math.abs(total - balanceNum) < 0.005;

  // Every payment belongs inside the statement window. The check runs on the
  // previewed dates, so it covers all five plans at once — a hand-typed custom
  // row, a single payment date and a count that overruns the gap all fail here.
  const windowIssue = useMemo(
    () =>
      planWindowIssue({
        payments,
        statementDate: form.statement_date,
        dueDate: form.due_date,
      }),
    [payments, form.statement_date, form.due_date],
  );

  // Only BLOCK when the preview came from this form. An existing statement whose
  // payment was moved past the due date on the cash-flow grid still shows the
  // warning, but editing its notes must not be held hostage by a date this modal
  // did not set — and re-planning anything flips `planDirty`, which re-arms it.
  const planFromForm = planDirty || storedSplits.length === 0;

  const outsideWindow = (iso) =>
    !!form.statement_date &&
    !!form.due_date &&
    !!iso &&
    (iso < form.statement_date || iso > form.due_date);

  // Money only moves on an Ontario banking day. The generated plans already skip
  // weekends and holidays, so what lands here is a hand-authored date — a custom
  // row or a single payment date typed onto Good Friday.
  const nonBanking = useMemo(() => planNonBankingDays(payments), [payments]);

  const warnings = useMemo(() => {
    const out = [];
    if (windowIssue) {
      const { before, after, gapDays } = windowIssue;
      const n = before.length + after.length;
      const sides = [
        before.length ? "before the statement date" : null,
        after.length ? "after the due date" : null,
      ]
        .filter(Boolean)
        .join(" and ");
      out.push(
        `${n} ${n === 1 ? "payment falls" : "payments fall"} ${sides}. ` +
          (needsCount
            ? `There ${gapDays === 1 ? "is" : "are"} only ${gapDays} day${gapDays === 1 ? "" : "s"} between the statement date and the due date — lower the count, or widen the window.`
            : "Every payment must fall on or between the statement date and the due date."),
      );
    }
    if (nonBanking.length) {
      const reasons = [...new Set(nonBanking.map((x) => x.reason))].join(", ");
      const n = nonBanking.length;
      out.push(
        `${n} ${n === 1 ? "payment lands" : "payments land"} on a day the bank does not move money (${reasons}). ` +
          `Move ${n === 1 ? "it" : "them"} to a weekday that is not an Ontario holiday.`,
      );
    }
    return out;
  }, [windowIssue, nonBanking, needsCount]);

  const blocking = planFromForm && (!!windowIssue || nonBanking.length > 0);

  const canSave =
    !!form.from_bank_account &&
    !!form.to_card_account &&
    !!form.statement_date &&
    !!form.due_date &&
    form.due_date > form.statement_date &&
    balanceNum > 0 &&
    (!needsCount || Number(form.plan_count) > 0) &&
    totalMatches &&
    !blocking;

  // ── custom split rows ──────────────────────────────────────────────────────
  function setCustomRow(i, key, val) {
    setPlanDirty(true);
    setForm((f) => ({
      ...f,
      custom: f.custom.map((row, idx) => (idx === i ? { ...row, [key]: val } : row)),
    }));
  }
  function addCustomRow() {
    setPlanDirty(true);
    setForm((f) => ({ ...f, custom: [...f.custom, { date: "", amount: "" }] }));
  }
  function removeCustomRow(i) {
    setPlanDirty(true);
    setForm((f) => ({ ...f, custom: f.custom.filter((_, idx) => idx !== i) }));
  }

  // `is_default` is deliberately ABSENT from the payload. Nothing in this modal
  // sets it any more — "Save + set default" writes a StatementPlan instead — and a
  // PATCH carrying false would demote a statement flagged elsewhere (Django admin)
  // just by editing an unrelated field. Omitted, a create takes the model default
  // and an edit leaves the value alone.
  function buildPayload() {
    return {
      from_bank_account: form.from_bank_account,
      to_card_account: form.to_card_account,
      balance: Number(form.balance).toFixed(2),
      statement_date: form.statement_date,
      due_date: form.due_date,
      payment_plan: form.payment_plan,
      plan_count: needsCount ? Number(form.plan_count) : null,
      splits: toSplitsPayload(payments),
      notes: form.notes ?? "",
    };
  }

  const footer = (
    <>
      <Button variant="outline" size="sm" onClick={onClose} disabled={saving}>
        Cancel
      </Button>
      {/* Saves the statement AND remembers its shape as this card's StatementPlan,
          so next month is just an amount.

          Note this no longer touches `is_default`. The label is kept because it is
          the one users know, but the mechanism behind it is the plan table — a
          template with no dates and no balance — rather than a flag on a dated
          statement that went stale the moment that month passed.

          Gated on `canSave` because the plan is derived from these fields — the
          dates supply the day-of-month pair — and on NOT being a custom split,
          which has no rule to reuse and which the API rejects as a plan. */}
      {onSaveWithPlan && (
        <Button
          variant="outline"
          size="sm"
          onClick={() => onSaveWithPlan(buildPayload(), formValuesToPlan(form), planForCard)}
          disabled={saving || savingPlan || !canSave || isCustom}
          title={
            isCustom
              ? "A custom split cannot be saved as a plan — there is no rule to reuse"
              : canSave
                ? planForCard
                  ? "Save this statement and replace the card's saved plan with these settings"
                  : "Save this statement and reuse these settings next month — you will only enter the amount"
                : "Complete the statement first; the plan is built from these fields"
          }
        >
          {savingPlan ? "Saving…" : "Save + set default"}
        </Button>
      )}
      <Button
        variant="primary"
        size="sm"
        onClick={() => onSave?.(buildPayload())}
        disabled={saving || !canSave}
        title={blocking ? warnings.join(" ") : undefined}
        className="font-semibold"
      >
        {/* "Save statement", not "Save plan" — with saved plans now a real thing,
            the old label named the wrong noun. */}
        {saving ? "Saving…" : isEdit ? "Save changes" : "Save statement"}
      </Button>
    </>
  );

  const lastDate = payments.length ? fmtPreviewDate(payments[payments.length - 1].date) : null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? "Edit statement" : "Add statement"}
      subtitle="Choose how this balance is paid; payments appear as outflows."
      size="lg"
      footer={footer}
    >
      <div className="space-y-5">
        {/* Says a plan filled this in, and which month the dates came from — a
            pre-filled form with no explanation reads as stale data. */}
        {appliedPlan && (
          <div className="flex items-start gap-2 rounded-xl border border-palette-indigo-border bg-palette-indigo-soft px-4 py-3 text-sm text-palette-indigo-text">
            <svg
              className="mt-0.5 h-4 w-4 shrink-0"
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
            >
              <path d="M4 8.5l2.5 2.5L12 5.5" strokeLinecap="round" strokeLinejoin="round" />
              <circle cx="8" cy="8" r="6.5" />
            </svg>
            <div>
              <p className="font-medium">
                Filled from this card&apos;s saved plan — enter the amount and save.
              </p>
              <p className="mt-0.5 text-xs">
                Statement day {appliedPlan.statement_day}, due day {appliedPlan.due_day}
                {month ? ` → ${month}` : ""}. Every field is still editable; changing one here does
                not alter the saved plan.
              </p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <SelectField
            label="From bank account"
            value={form.from_bank_account}
            onChange={set("from_bank_account")}
            options={bankOptions}
            placeholder="Select bank account"
          />
          <SelectField
            label="To card account"
            value={form.to_card_account}
            onChange={set("to_card_account")}
            options={cardOptions}
            placeholder="Select card account"
          />
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <DateField
            label="Statement date"
            value={form.statement_date}
            onChange={set("statement_date")}
          />
          <DateField
            label="Due date"
            value={form.due_date}
            onChange={setPlan("due_date")}
            min={minDueDate}
          />
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <MoneyField
            label={`Balance${selectedCard?.currency ? ` (${selectedCard.currency})` : ""}`}
            value={form.balance}
            onChange={setPlan("balance")}
          />
          <SelectField
            label="Payment plan"
            value={form.payment_plan}
            onChange={setPlan("payment_plan")}
            options={PAYMENT_PLANS}
          />
        </div>

        {/* Plan-specific control. Each plan needs at most one extra input, so it
            takes the same slot rather than the form growing a field per plan. */}
        {needsCount && (
          <div>
            <Label>{COUNT_LABELS[form.payment_plan]}</Label>
            <input
              type="number"
              inputMode="numeric"
              min="1"
              value={form.plan_count}
              onChange={(e) => setPlan("plan_count")(e.target.value)}
              placeholder="e.g. 3"
              className={FIELD_CLS}
            />
          </div>
        )}
        {isSingle && (
          <DateField
            label="Payment date"
            value={form.single_date || defaultSingleDate}
            onChange={setPlan("single_date")}
            min={form.statement_date}
            max={form.due_date}
          />
        )}

        {isCustom && (
          <div>
            <Label>Custom payments</Label>
            <div className="space-y-2">
              {form.custom.map((row, i) => (
                <div key={i} className="flex items-center gap-2">
                  <div className="flex-1">
                    <DateField
                      value={row.date}
                      onChange={(v) => setCustomRow(i, "date", v)}
                      min={form.statement_date}
                      max={form.due_date}
                    />
                  </div>
                  <div className="flex-1">
                    <input
                      type="number"
                      inputMode="decimal"
                      min="0"
                      step="0.01"
                      value={row.amount}
                      onChange={(e) => setCustomRow(i, "amount", e.target.value)}
                      placeholder="Amount"
                      className={FIELD_CLS}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => removeCustomRow(i)}
                    title="Remove payment"
                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-palette-gray-divider text-palette-gray-secondary transition-colors hover:bg-palette-gray-subtle"
                  >
                    <svg
                      className="h-3.5 w-3.5"
                      viewBox="0 0 12 12"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M2 2l8 8M10 2l-8 8" strokeLinecap="round" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={addCustomRow}
              className="mt-2 rounded-xl border border-dashed border-palette-gray-divider px-4 py-2 text-sm font-medium text-palette-gray-heading transition-colors hover:bg-palette-gray-subtle"
            >
              + Add payment
            </button>
          </div>
        )}

        {warnings.length > 0 && (
          <div className="flex items-start gap-2 rounded-xl border border-palette-amber-border bg-palette-amber-soft px-4 py-3 text-sm text-palette-amber-primary">
            <svg
              className="mt-0.5 h-4 w-4 shrink-0"
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
            >
              <path
                d="M8 1.75l6.25 11.5H1.75L8 1.75zM8 6.25v3.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <circle cx="8" cy="11.5" r="0.6" fill="currentColor" stroke="none" />
            </svg>
            <div className="space-y-1">
              {warnings.map((w, i) => (
                <p key={i}>{w}</p>
              ))}
              {!planFromForm && <p>Saving is still allowed because the plan was not changed.</p>}
            </div>
          </div>
        )}

        {/* Resulting payments — recomputed on every change. */}
        <div className="overflow-hidden rounded-xl border border-palette-gray-divider bg-palette-gray-subtle">
          <div className="flex items-center justify-between gap-3 px-4 py-2.5">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-palette-gray-secondary">
              Resulting payments · {payments.length}{" "}
              {payments.length === 1 ? "payment" : "payments"}
            </span>
            {lastDate && (
              <span className="text-xs text-palette-gray-secondary">Last {lastDate}</span>
            )}
          </div>
          {payments.length === 0 ? (
            <div className="px-4 pb-4 text-sm text-palette-gray-placeholder">
              Enter a balance and a plan to see the payments.
            </div>
          ) : (
            <div className="bg-white">
              {payments.map((p, i) => {
                // Flagged per row, so a 12-payment plan shows WHICH payments are
                // wrong and why, rather than only that some are.
                const closed = nonBankingReason(p.date);
                const problem = outsideWindow(p.iso)
                  ? "Outside the statement → due window"
                  : closed
                    ? `Falls on ${closed} — the bank does not move money`
                    : null;
                return (
                  <div
                    key={i}
                    className={`flex items-center justify-between gap-3 border-t border-palette-gray-divider px-4 py-2 text-sm ${
                      problem ? "bg-palette-amber-soft" : ""
                    }`}
                    title={problem ?? undefined}
                  >
                    <span
                      className={`flex items-center gap-2 ${
                        problem
                          ? "font-medium text-palette-amber-primary"
                          : "text-palette-gray-heading"
                      }`}
                    >
                      <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-smiles-redOrange" />
                      {fmtPreviewDate(p.date)}
                      {closed && !outsideWindow(p.iso) && (
                        <span className="text-xs font-normal">({closed})</span>
                      )}
                    </span>
                    <span
                      className={`tabular-nums font-medium ${
                        problem ? "text-palette-amber-primary" : "text-palette-gray-primary"
                      }`}
                    >
                      -{formatMoney(Math.abs(p.amount))}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
          <div
            className={`flex items-center justify-between gap-3 border-t border-palette-gray-divider px-4 py-2.5 text-sm font-semibold ${
              totalMatches ? "bg-palette-emerald-soft" : "bg-palette-rose-soft"
            }`}
          >
            <span className="text-palette-gray-primary">Plan total</span>
            <span
              className={`tabular-nums ${
                totalMatches ? "text-smiles-green" : "text-smiles-redOrange"
              }`}
              title={
                totalMatches
                  ? "The plan accounts for the full balance"
                  : "The payments must add up to the balance before this can be saved"
              }
            >
              {formatMoney(total)} / {formatMoney(balanceNum)}
            </span>
          </div>
        </div>

        {selectedCard && (
          <div className="flex items-center gap-2 text-xs text-palette-gray-secondary">
            Paying
            <span className="font-medium text-palette-gray-heading">{selectedCard.label}</span>
            <CurrencyBadge currency={selectedCard.currency} />
          </div>
        )}
      </div>
    </Modal>
  );
}
